import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { classnames as cx } from '../../utilities';
import './Tooltip.scss';

export const Portal = ({ component, open }) => {
  if (!open) {
    return null;
  }

  let target = document.querySelector('#tooltip-root');
  if (!target) {
    target = document.createElement('div');
    target.id = 'tooltip-root';
    document.body.appendChild(target);
  }

  return createPortal(component, target);
};

const createTooltip = (component, direction, offset, className, open) => {
  if (!open) {
    return null;
  }

  const captureClick = event => {
    event.stopPropagation();
  };

  const pageYOffset = window.pageYOffset;
  const pageXOffset = window.pageXOffset;

  const offsetY = offset.y + pageYOffset + offset.height + 5;

  let offsetX = offset.x + pageXOffset;
  if (direction === 'right') {
    offsetX = offsetX + offset.width - 235;
  }

  const classes = cx('tooltip', direction, { [className]: className });
  const style = { top: offsetY, left: offsetX };

  return (
    <div
      className={classes}
      style={style}
      onClick={captureClick}
      data-qa="create-tooltip"
    >
      {component}
    </div>
  );
};

const pathIncludesTooltip = (
  path: (EventTarget & Partial<HTMLElement>)[] = []
) => {
  if (path.length === 0) {
    return false;
  }

  const classNames = path.reduce((prev, el) => {
    if (el.className) {
      prev += el.className;
    }
    return prev;
  }, '');

  return classNames.length && /(tooltip(?!(-anchor))).*/.test(classNames);
};

const pathIncludesAnchor = (
  path: (EventTarget & Partial<HTMLElement>)[] = []
) => {
  if (path.length === 0) {
    return false;
  }

  const classNames = path.reduce((prev, el) => {
    if (el.className) {
      prev += el.className;
    }
    return prev;
  }, '');

  return classNames.length && /(tooltip-anchor).*/.test(classNames);
};

const noop = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function

type OwnProps = React.PropsWithChildren<{
  className?: string;
  component: React.ReactElement;
  onClick: (ev: MouseEvent) => void;
  open?: boolean;
  qaName?: string;
  right?: boolean;
}>;

export default function Tooltip({
  children,
  className,
  component,
  onClick = noop,
  open = false,
  qaName = '',
  right = false
}: OwnProps): JSX.Element {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setElement(event.currentTarget);

      const { x, y, width, height } =
        event.currentTarget.getBoundingClientRect();
      setOffset({ x, y, width, height });
      onClick(event.nativeEvent);
    },
    [onClick]
  );

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();

      if (!pathIncludesTooltip(event.composedPath())) {
        onClick(event);
      }

      if (pathIncludesAnchor(event.composedPath())) {
        event.stopPropagation();
      }
    },
    [onClick]
  );

  const handleScrollEvent = useCallback(() => {
    if (!open || element == null) return;
    const { x, y, width, height } = element.getBoundingClientRect();
    setOffset({ x, y, width, height });
  }, [element, open]);

  useEffect(() => {
    if (open) {
      document.body.addEventListener('click', handleOutsideClick);
    }

    return () => {
      document.body.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick, open]);

  useEffect(() => {
    if (element) {
      document.addEventListener('scroll', handleScrollEvent);
    } else {
      document.removeEventListener('scroll', handleScrollEvent);
    }

    return () => {
      document.removeEventListener('scroll', handleScrollEvent);
    };
  }, [element, handleScrollEvent]);

  const tooltip = useMemo(
    () =>
      createTooltip(
        component,
        right ? 'right' : 'left',
        offset,
        className,
        open
      ),
    [className, component, offset, open, right]
  );

  const dataQa = useMemo(
    () => [qaName, 'tooltip'].filter(Boolean).join('-'),
    [qaName]
  );

  return (
    <span className="tooltip-anchor" onClick={handleClick} data-qa={dataQa}>
      {children}
      <Portal component={tooltip} open={open} />
    </span>
  );
}
